import { __, sprintf } from "@wordpress/i18n";
import { useState, useEffect, createPortal } from "@wordpress/element";
import { AlertTriangle, CheckCircle2, Check, X, Lock } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { GALLERY_LAYOUTS } from "../../constants/galleryLayouts";
import { isPremium } from "../../utils/isPremium";

function ApplyModal({ layout, onConfirm, onCancel }) {
    const portalTarget = document.getElementById("wpbean-pgs-setting-rule-app");

    const modal = (
        <>
            <div
                className="fixed inset-0 z-40 bg-black/40"
                onClick={onCancel}
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                    className="w-full max-w-md rounded-xl bg-white shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-6">
                        <h2 className="text-[15px] font-semibold text-gray-900">
                            {sprintf(__('Apply "%s" layout?', "wpbean-product-gallery-slider-for-woocommerce"), layout.title)}
                        </h2>
                        <p className="mt-2 text-[13px] leading-relaxed text-gray-600">
                            {sprintf(
                                __("This will replace your current gallery configuration with the %s preset defaults. Settings in other tabs (Zoom, Lightbox, Mobile, Video) will not be affected.", "wpbean-product-gallery-slider-for-woocommerce"),
                                layout.title,
                            )}
                        </p>

                        {/* Backup warning */}
                        <div className="mt-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                            <div>
                                <p className="text-[12px] font-semibold text-amber-800">
                                    {__("Back up your current setup first", "wpbean-product-gallery-slider-for-woocommerce")}
                                </p>
                                <p className="mt-0.5 text-[12px] leading-relaxed text-amber-700">
                                    {__("If your current gallery configuration is important, save this rule and use Duplicate (⋯ menu) to create a backup copy before applying. You can undo with Ctrl+Z before saving, but changes are permanent once saved.", "wpbean-product-gallery-slider-for-woocommerce")}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="rounded-lg border border-gray-200 px-4 py-2 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
                        >
                            {__("Cancel", "wpbean-product-gallery-slider-for-woocommerce")}
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 active:bg-blue-800"
                        >
                            {__("Apply Layout", "wpbean-product-gallery-slider-for-woocommerce")}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );

    return portalTarget ? createPortal(modal, portalTarget) : modal;
}

export default function QuickStartPanel() {
    const { actions } = useSettings();
    const [pending, setPending] = useState(null);
    const [appliedId, setAppliedId] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);

    // Auto-dismiss the success banner after 6 s
    useEffect(() => {
        if (!showSuccess) return;
        const t = setTimeout(() => setShowSuccess(false), 6000);
        return () => clearTimeout(t);
    }, [showSuccess]);

    const handleConfirm = () => {
        actions.applyLayout(pending.config);
        setAppliedId(pending.id);
        setShowSuccess(true);
        setPending(null);
    };

    const appliedLayout = GALLERY_LAYOUTS.find((l) => l.id === appliedId);

    return (
        <>
            {/* Success banner */}
            {showSuccess && appliedLayout && (
                <div className="mb-5 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-emerald-800">
                            "{appliedLayout.title}" layout applied
                        </p>
                        <p className="mt-0.5 text-[12px] leading-relaxed text-emerald-700">
                            Gallery settings have been updated. Click{" "}
                            <strong>Save Changes</strong> in the top bar to
                            save, or visit the <strong>Gallery</strong> tab to
                            fine-tune the options.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowSuccess(false)}
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-emerald-400 transition-colors hover:text-emerald-600"
                        aria-label="Dismiss"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}

            {/* Intro */}
            <div className="mb-5">
                <p className="text-[13px] leading-relaxed text-gray-600">
                    Pick a preset to instantly configure your gallery layout.
                    You can fine-tune every option in the{" "}
                    <strong>Gallery</strong> tab afterwards. Only
                    gallery-related settings will be changed.
                </p>
            </div>

            {/* Layout grid */}
            <div className="grid grid-cols-1 wpxs:grid-cols-2 wpsm:grid-cols-3 gap-4">
                {GALLERY_LAYOUTS.map((layout) => {
                    const { id, title, description, Icon } = layout;
                    const isApplied = appliedId === id;
                    const isLocked = layout.premium && !isPremium();
                    const upgradeUrl = window.WPBean_PGS_Options?.upgradeUrl || "#";
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => {
                                if (isLocked) {
                                    window.open(upgradeUrl, "_blank", "noopener,noreferrer");
                                } else {
                                    setPending(layout);
                                }
                            }}
                            title={isLocked ? __("Available in Pro — click to upgrade", "wpbean-product-gallery-slider-for-woocommerce") : undefined}
                            className={[
                                "group relative flex flex-col overflow-hidden rounded-xl border bg-white text-start",
                                "transition-all duration-200",
                                "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                                isApplied
                                    ? "border-emerald-400 shadow-md"
                                    : isLocked
                                        ? "border-gray-200 hover:border-amber-300 hover:shadow-md"
                                        : "border-gray-200 hover:border-blue-300 hover:shadow-md",
                            ].join(" ")}
                        >
                            {/* SVG preview */}
                            <div
                                className={[
                                    "flex items-center justify-center px-6 py-5 transition-colors",
                                    isApplied
                                        ? "bg-emerald-50"
                                        : isLocked
                                            ? "bg-gray-50 group-hover:bg-amber-50/40"
                                            : "bg-gray-50 group-hover:bg-blue-50/40",
                                ].join(" ")}
                            >
                                <div className={isLocked ? "opacity-40" : ""}>
                                    <Icon />
                                </div>
                            </div>

                            {/* Label */}
                            <div className="flex-1 px-3 py-2.5">
                                <p
                                    className={[
                                        "text-[12.5px] font-semibold",
                                        isApplied
                                            ? "text-emerald-700"
                                            : isLocked
                                                ? "text-gray-400"
                                                : "text-gray-900 group-hover:text-blue-700",
                                    ].join(" ")}
                                >
                                    {title}
                                </p>
                                <p className={["mt-0.5 text-[11px] leading-snug", isLocked ? "text-gray-300" : "text-gray-500"].join(" ")}>
                                    {description}
                                </p>
                            </div>

                            {/* Applied badge */}
                            {isApplied && (
                                <span className="absolute end-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500">
                                    <Check
                                        className="h-3 w-3 text-white"
                                        strokeWidth={2.5}
                                    />
                                </span>
                            )}

                            {/* Pro badge */}
                            {isLocked && (
                                <span className="absolute end-2 top-2 inline-flex items-center gap-0.5 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-600">
                                    <Lock size={8} strokeWidth={2.5} />
                                    Pro
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Confirmation modal */}
            {pending && (
                <ApplyModal
                    layout={pending}
                    onConfirm={handleConfirm}
                    onCancel={() => setPending(null)}
                />
            )}
        </>
    );
}
